/**
 * Schema.org, meta tags e helpers SEO para posts do blog.
 */

import {
  truncateSeoTitle,
  truncateSeoDescription,
  normalizeProductionUrl,
  toSchemaIsoDate,
  stripUndefinedDeep,
} from './metaHelpers';

export function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function resolveBlogOgImage(post) {
  if (!post) return null;
  const cover = post.imageUrl || post.image2Url || post.image3Url || null;
  if (post.useCoverImageForSeo !== false) {
    return post.ogImageUrl || post.seoImageUrl || cover;
  }
  return post.ogImageUrl || post.seoImageUrl || cover;
}

export function buildBlogMetaKeywords(post) {
  const parts = [];
  if (post?.seoKeywords?.trim()) parts.push(post.seoKeywords.trim());
  if (post?.seoKeywordsSecondary?.trim()) parts.push(post.seoKeywordsSecondary.trim());
  if (!parts.length && post?.tags?.trim()) parts.push(post.tags.trim());
  if (!parts.length && post?.category) parts.push(post.category);
  return parts.join(', ');
}

export function buildDynamicBlogDescription(post, brand) {
  if (post?.seoMetaDescription?.trim()) {
    return truncateSeoDescription(post.seoMetaDescription);
  }
  if (post?.conclusion?.trim()) {
    return truncateSeoDescription(post.conclusion);
  }
  const fromContent = stripHtml(post?.content);
  if (fromContent) return truncateSeoDescription(fromContent);
  return truncateSeoDescription(
    `${post?.title || 'Artigo'}. Leia no blog${brand ? ` — ${brand}` : ''}.`
  );
}

export function resolveBlogCanonicalUrl(post, baseUrl, fallbackId) {
  const slug = post?.slug || fallbackId;
  const defaultUrl = `${baseUrl.replace(/\/$/, '')}/blog/${slug}`;
  return normalizeProductionUrl(post?.canonicalUrl || defaultUrl, baseUrl);
}

export function resolveBlogRobotsString(post) {
  if (!post) return 'index,follow,max-image-preview:large';
  if (post.status === 'draft') return 'noindex,nofollow';
  const parts = [post.robotsIndex === 'noindex' ? 'noindex' : 'index'];
  parts.push(post.robotsFollow === false ? 'nofollow' : 'follow');
  if (post.robotsIndex !== 'noindex') parts.push('max-image-preview:large');
  if (post.robotsArchive) parts.push('noarchive');
  return parts.join(',');
}

export function countWords(text) {
  const plain = stripHtml(text);
  if (!plain) return 0;
  return plain.split(/\s+/).filter(Boolean).length;
}

/** Extrai pares pergunta/resposta de blocos FAQ no HTML (h3/h4 + parágrafo). */
export function extractFaqFromContent(html) {
  if (!html || typeof html !== 'string') return [];
  const items = [];
  const blockRegex = /<(h[34])[^>]*>([\s\S]*?)<\/\1>\s*(<p[^>]*>[\s\S]*?<\/p>)/gi;
  let match;
  while ((match = blockRegex.exec(html)) !== null) {
    const question = stripHtml(match[2]);
    const answer = stripHtml(match[3]);
    if (question && answer && question.length < 200) {
      items.push({ question, answer });
    }
  }
  return items.slice(0, 10);
}

export function buildBlogPostingJsonLd({
  post,
  settings = {},
  baseUrl,
  canonicalUrl,
  metaTitle,
  metaDescription,
  ogImage,
}) {
  if (!post) return null;

  const schemaType = post.schemaType || 'BlogPosting';
  const articleBody = stripHtml(post.content);
  const wordCount = countWords(post.content);

  const schema = stripUndefinedDeep({
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': canonicalUrl,
    headline: post.title,
    name: metaTitle || post.title,
    description: metaDescription,
    url: canonicalUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    datePublished: toSchemaIsoDate(post.createdAt),
    dateModified: toSchemaIsoDate(post.updatedAt || post.createdAt),
    image: ogImage ? [ogImage] : undefined,
    articleBody: articleBody || undefined,
    wordCount: wordCount || undefined,
    keywords: buildBlogMetaKeywords(post) || undefined,
    articleSection: post.category || undefined,
    inLanguage: 'pt-BR',
    author: settings.companyName
      ? { '@type': 'Organization', name: settings.companyName, url: baseUrl }
      : undefined,
    publisher: settings.companyName
      ? {
          '@type': 'Organization',
          name: settings.companyName,
          url: baseUrl,
          ...(settings.logo ? { logo: { '@type': 'ImageObject', url: settings.logo } } : {}),
        }
      : undefined,
  });

  return schema;
}

export function buildBlogFaqSchema(post) {
  if (post?.schemaFaq === false) return null;
  const items = extractFaqFromContent(post.content);
  if (!items.length) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
}

export function resolveBlogSeoMeta(post, settings, baseUrl, fallbackId) {
  const brand = settings?.companyName || 'Blog';
  const rawTitle = post?.seoMetaTitle || post?.title || `${brand} - Post`;
  const metaTitle = truncateSeoTitle(`${rawTitle}${rawTitle.includes(brand) ? '' : ` - ${brand}`}`.trim());
  const metaDescription = buildDynamicBlogDescription(post, brand);
  const metaKeywords = buildBlogMetaKeywords(post);
  const ogImageRaw = resolveBlogOgImage(post);
  const ogImage = ogImageRaw ? normalizeProductionUrl(ogImageRaw, baseUrl) : null;
  const canonicalUrl = resolveBlogCanonicalUrl(post, baseUrl, fallbackId);

  return {
    metaTitle,
    metaDescription,
    metaKeywords,
    ogTitle: truncateSeoTitle(post?.ogTitle || rawTitle, 95),
    ogDescription: truncateSeoDescription(post?.ogDescription || metaDescription, 200),
    ogImage,
    twitterTitle: truncateSeoTitle(post?.twitterTitle || post?.ogTitle || rawTitle, 70),
    twitterDescription: truncateSeoDescription(
      post?.twitterDescription || post?.ogDescription || metaDescription,
      200
    ),
    twitterImage: normalizeProductionUrl(
      post?.twitterImageUrl || post?.ogImageUrl || ogImageRaw,
      baseUrl
    ),
    twitterImageAlt: post?.twitterImageAlt || post?.ogImageAlt || metaTitle,
    twitterCard: post?.twitterCard || 'summary_large_image',
    canonicalUrl,
    robots: resolveBlogRobotsString(post),
  };
}
